// Lib
import { useCallback, useRef } from 'react';
import type { MutableRefObject } from 'react';
import { useDispatch } from 'react-redux';
import { cloneDeep, isEmpty, isEqual, omit } from 'lodash';
import Handsontable from 'handsontable/base';
import type { GridSettings } from 'handsontable/settings';
import type { SimpleCellAddress, SimpleCellRange } from 'hyperformula';
import { CellType } from 'hyperformula';

// Utils
import { hasCommandModifier, isControlKeyCommand } from '../../../utils/keyboard/keyboardUtility';
import { getFirstSelection, getStartCol, getStartRow } from '../utils/selectionUtils/tableSelectionAccessors';
import { getAllCellsBetween } from '../utils/selectionUtils/tableSelectionGeometryUtils';
import {
    hasSelections,
    isSelectingSingleCell,
    isSelectingSingleSelection,
} from '../utils/selectionUtils/tableSelectionPredicateUtils';
import {
    getCellRangeFromStartAndSize,
    getCellSelectionFromRange,
} from '../utils/selectionUtils/tableSelectionRangeUtils';
import { repositionAutofillHandle } from '../utils/hotInstanceUtils/selection/hotTableSelectionUiUtils';
import { adjustTableContentDataSize } from '../utils/dataUtils/grid/tableGridSizingUtils';
import { normaliseComputedCellValue } from '../utils/dataUtils/cell/tableCellRenderValueUtils';
import { convertCellDataToReduxFormat } from '../utils/dataUtils/persistence/tablePersistenceValueUtils';
import { getTableElementFromHotInstance } from '../utils/domUtils/hotTableDomAccessorUtils';
import HotTableClipboardManager from '../manager/HotTableClipboardManager';
import hyperFormulaInstance from '../manager/hyperFormulaInstance';
import useRefCallback from '../../../utils/react/useRefCallback';
import { getColumnCount, getRowCount } from '../../../../common/table/utils/tableCellDataPropertyUtils';
import { buildHotTableContentData } from '../utils/hotInstanceUtils/hotTableDataUtils';
import { inferFormulaCellType } from '../utils/hotInstanceUtils/cell/hotTableFormulaTypeInferenceUtils';
import { convertHotInstanceToRaw } from '../utils/hotInstanceUtils/persistence/hotTablePersistenceUtils';
import {
    captureHyperFormulaUndoRedoStack,
    runWithoutChangingHyperFormulaUndoRedoStack,
    type HyperFormulaUndoRedoStacks,
} from '../utils/hotInstanceUtils/undoRedo/hotTableUndoRedoHyperFormulaUtils';
import { reconcileMovedGridWithUndoRedo } from '../utils/hotInstanceUtils/undoRedo/hotTableUndoRedoMoveUtils';
import {
    createCellDataChanges,
    createCellDataChangesFromReplay,
    createCellDataChangesFromSource,
} from '../utils/hotInstanceUtils/cell/hotTableCellEditingUtils';
import { applyHotTableCellMetaInRange } from '../utils/hotInstanceUtils/meta/hotTableMetaRangeUtils';
import { continueCellFormatting } from '../utils/dataUtils/grid/tableGridFormattingContinuationUtils';
import { isTableCellMetaChangeSource } from '../../../../common/table/utils/tableChangeSourceUtils';
import { setHotTableCellMetaObject } from '../utils/hotInstanceUtils/meta/hotTableCellMetaUtils';
import { applyExactHotTableCellDataChanges } from '../utils/hotInstanceUtils/hotTableUpdateUtils';
import {
    clearUndoRedoCellDataReplay,
    getUndoRedoReplayCellData,
    setPendingUndoRedoActionCellDataChanges,
    type UndoRedoRichCellDataState,
} from '../utils/hotInstanceUtils/undoRedo/hotTableUndoRedoRichCellDataUtils';

// Actions
import { removeSelectedElements } from '../../selection/selectionActions';

// Types
import type { RawDraftContentState } from 'draft-js';
import type { CellData, CellDataChange, CellSelection, MNTableContent } from '../../../../common/table/TableTypes';
import { TableAxis, TableChangeSource, TableOperation } from '../../../../common/table/TableTypes';

type HotTableHook<Key extends keyof GridSettings> = NonNullable<GridSettings[Key]>;

export type UseTableCellEditingHandlersReturn = {
    beforeAutofill: HotTableHook<'beforeAutofill'>;
    beforeCopy: HotTableHook<'beforeCopy'>;
    beforeCut: HotTableHook<'beforeCut'>;
    beforePaste: HotTableHook<'beforePaste'>;
    beforeChange: HotTableHook<'beforeChange'>;
    beforeGridMovement: (axis: TableAxis) => HotTableHook<'beforeRowMove'>;
    afterChange: HotTableHook<'afterChange'>;
    afterGridInsert: (axis: TableAxis) => HotTableHook<'afterCreateRow'>;
    afterGridRemove: (axis: TableAxis) => HotTableHook<'afterRemoveRow'>;
    afterGridMovement: (axis: TableAxis) => HotTableHook<'afterRowMove'>;
    beforeOnCellMouseDown: HotTableHook<'beforeOnCellMouseDown'>;
};

type HotMouseEvent = MouseEvent & {
    // Handsontable private property
    isImmediatePropagationEnabled?: boolean;
};

type FormulaCellDataChanges = Partial<Pick<CellData, 'type' | 'resultValue'>>;
type HotCopyPasteSelection = {
    startRow: number;
    startCol: number;
    endRow: number;
    endCol: number;
};
type NumericHotCellChange = [number, number, Handsontable.CellValue, Handsontable.CellValue];

const isSimpleCellAddress = (cellAddress: SimpleCellAddress | SimpleCellRange): cellAddress is SimpleCellAddress =>
    'row' in cellAddress && 'col' in cellAddress;

/**
 * Returns draft editor content only for direct editor commits.
 *
 * Handsontable replays undo/redo values through `changes`, so reusing the currently mounted editor content for
 * `UndoRedo.undo`/`UndoRedo.redo` can overwrite those replayed values with stale text from the last edited cell.
 */
export const getTableEditorDraftContentForChangeSource = (
    source: string | undefined,
    activeEditor: { content?: RawDraftContentState } | undefined,
): RawDraftContentState | undefined => {
    if (source !== 'edit') return;

    return activeEditor?.content;
};

/**
 * Custom hook for handling table cell editing operations
 */
export const useTableCellEditingHandlers = ({
    elementId,
    locale,
    gridSize,
    currencyPreference,
    hotTableInstanceRef,
    hotTableContainerRef,
    isSingleSelected,
    undoRedoRichCellDataState,
}: {
    elementId: string;
    locale: string;
    gridSize: number;
    currencyPreference: string;
    hotTableInstanceRef: MutableRefObject<Handsontable | null>;
    hotTableContainerRef: MutableRefObject<HTMLDivElement | null>;
    isSingleSelected: boolean;
    undoRedoRichCellDataState: UndoRedoRichCellDataState;
}): UseTableCellEditingHandlersReturn => {
    const dispatch = useDispatch();

    const pendingBeforeMoveRef = useRef<{
        snapshot: MNTableContent;
        hyperFormulaStack: HyperFormulaUndoRedoStacks | null;
    } | null>(null);

    const dispatchDeselectElement = useCallback(
        (elId: string) => dispatch(removeSelectedElements({ ids: [elId], transactionId: undefined })),
        [dispatch],
    );

    /**
     * Captures the source range before autofill runs so the fill operation can reuse the original cell data.
     */
    const beforeAutofill = useRefCallback<UseTableCellEditingHandlersReturn['beforeAutofill']>(
        (_selectionData, sourceRange) => {
            HotTableClipboardManager.setTableAutofillSource(
                hotTableInstanceRef,
                getCellSelectionFromRange(sourceRange),
            );
        },
        [hotTableInstanceRef],
    );

    /**
     * Records the current copy selection so later paste flows can recover the copied table cell data.
     */
    const beforeCopy = useRefCallback<UseTableCellEditingHandlersReturn['beforeCopy']>(
        (_data, coords) => {
            const selection = coords[0] as HotCopyPasteSelection | undefined;
            if (!selection) return;

            const { startRow, startCol, endRow, endCol } = selection;

            HotTableClipboardManager.setTableCopySource(hotTableInstanceRef, [startRow, startCol, endRow, endCol]);
        },
        [hotTableInstanceRef],
    );

    /**
     * Reuses the copy bookkeeping for cut operations, then temporarily clears read-only flags so HOT can remove
     * the cut cell contents.
     */
    const beforeCut = useRefCallback<UseTableCellEditingHandlersReturn['beforeCut']>(
        (data, coords) => {
            const hotInstance = hotTableInstanceRef.current;
            if (!hotInstance) return;

            beforeCopy(data, coords, { columnHeadersCount: 0 });

            // Revert readOnly cells to be editable so that they can be cleared on cut
            const selection = coords[0] as HotCopyPasteSelection | undefined;
            if (!selection) return;

            const { startRow, startCol, endRow, endCol } = selection;
            getAllCellsBetween([[startRow, startCol, endRow, endCol]]).forEach(({ row, col }) => {
                hotInstance.setCellMetaObject(row, col, { readOnly: false });
            });
        },
        [beforeCopy, hotTableInstanceRef],
    );

    /**
     * Makes the destination range editable before paste so protected cell metadata does not block the incoming data.
     */
    const beforePaste = useRefCallback<UseTableCellEditingHandlersReturn['beforePaste']>(
        (data, coords) => {
            const hotInstance = hotTableInstanceRef.current;
            if (!hotInstance) return;

            const selection = coords[0] as HotCopyPasteSelection | undefined;
            if (!selection) return;

            const { startRow, startCol } = selection;

            const nRows = getRowCount(data);
            const nCols = getColumnCount(data);

            // Revert readOnly cells to be editable so that they can be pasted on to
            const pastedCellSelection: CellSelection = [startRow, startCol, startRow + nRows, startCol + nCols];
            getAllCellsBetween([pastedCellSelection]).forEach(({ row, col }) => {
                hotInstance.setCellMetaObject(row, col, { readOnly: false });
            });
        },
        [hotTableInstanceRef],
    );

    /**
     * Normalises incoming HOT edits into Milanote cell data before HOT commits them.
     *
     * Meta-only change sources are allowed through unchanged so existing persisted cell data stays intact. For user,
     * paste, and autofill edits, this hook rebuilds rich cell payloads, expands the grid if the change reaches beyond
     * the current bounds, rewrites HOT's pending values to the resolved cell values, and mirrors the updated cell data
     * into HOT cell meta so follow-up handlers read the same canonical state.
     */
    const beforeChange = useRefCallback<UseTableCellEditingHandlersReturn['beforeChange']>(
        (changes, source) => {
            const hotInstance = hotTableInstanceRef.current;
            if (!hotInstance || !changes?.length) return;

            // If source is coming from updating cell meta, save data as is
            if (isTableCellMetaChangeSource(source)) return true;

            const hotChanges = changes as Handsontable.CellChange[];
            const numericHotChanges = hotChanges as NumericHotCellChange[];
            const data = buildHotTableContentData(hotInstance);

            // Convert cell changes [row, col, prevValue, newValue] to [row, col, prevCellData, newCellData]
            let cellDataChanges: CellDataChange[];

            // Step 4 of undo/redo flow
            // For undo / redo - we need to get the data that was retrieved from the undo action
            //  and apply it to the cell data changes
            if (source === TableChangeSource.UNDO || source === TableChangeSource.REDO) {
                const replayCellDataByCoordinate = new Map<string, CellData>();

                hotChanges.forEach(([row, col]) => {
                    const replayCellData = getUndoRedoReplayCellData(undoRedoRichCellDataState, row, Number(col));
                    if (!replayCellData) return;

                    replayCellDataByCoordinate.set(`${row}:${Number(col)}`, replayCellData);
                });

                cellDataChanges = createCellDataChangesFromReplay(
                    hotChanges,
                    data,
                    replayCellDataByCoordinate,
                    locale,
                    currencyPreference,
                );
            } else if (source === TableChangeSource.AUTOFILL_FILL || source === TableChangeSource.COPY_PASTE_PASTE) {
                const sourceCellDataArray =
                    source === TableChangeSource.AUTOFILL_FILL
                        ? HotTableClipboardManager.getTableAutofillData()
                        : HotTableClipboardManager.getTableCopiedDataForChanges(hotChanges);

                cellDataChanges = createCellDataChangesFromSource(
                    numericHotChanges,
                    data,
                    source,
                    sourceCellDataArray,
                    locale,
                ) as CellDataChange[];
            } else {
                // Retrieve draft editor content from the active editor
                const activeEditor = hotInstance.getActiveEditor() as { content?: RawDraftContentState } | undefined;
                const tableEditorDraftContent = getTableEditorDraftContentForChangeSource(source, activeEditor);

                cellDataChanges = createCellDataChanges(
                    hotChanges,
                    data,
                    locale,
                    currencyPreference,
                    tableEditorDraftContent,
                );
            }

            // Adjust table size to fit all the changes
            const nRows = getRowCount(data);
            const nCols = getColumnCount(data);

            const maxChangeRow = Math.max(...cellDataChanges.map(([row]) => row)) + 1;
            const maxChangeCol = Math.max(...cellDataChanges.map(([, col]) => Number(col))) + 1;

            const maxRow = Math.max(maxChangeRow, nRows);
            const maxCol = Math.max(maxChangeCol, nCols);

            const newData = adjustTableContentDataSize(data, maxRow, maxCol);

            const nRowsNew = getRowCount(newData) - nRows;
            const nColsNew = getColumnCount(newData) - nCols;

            if (nRowsNew > 0) {
                hotInstance.alter('insert_row_below', nRows - 1, nRowsNew, source);
            }

            if (nColsNew > 0) {
                hotInstance.alter('insert_col_end', nCols - 1, nColsNew, source);
            }

            // Apply changes if any
            cellDataChanges.forEach((cellDataChange, index) => {
                const [row, col, _previousCellData, newCellData] = cellDataChange;
                const numericCol = Number(col);

                hotChanges[index][3] = newCellData.value;
                newData[row][numericCol] = newCellData;

                setHotTableCellMetaObject(hotInstance, row, numericCol, { cellData: newCellData });
            });

            if (isEqual(newData, data)) return;

            // Step 1 of the undo/redo flow
            //  Store the changes associated with the HOT instance so it can be used in a future undo/redo action
            setPendingUndoRedoActionCellDataChanges(undoRedoRichCellDataState, cellDataChanges);
        },
        [currencyPreference, hotTableInstanceRef, locale, undoRedoRichCellDataState],
    );

    /**
     * After a change is made, for formula cells:
     * 1. infer formula cell's cell type based on precedents cells (if it is not already set)
     * 2. Update the Handsontable instance with those changes
     */
    const afterChange = useRefCallback<UseTableCellEditingHandlersReturn['afterChange']>(
        (changes, source) => {
            const hotInstance = hotTableInstanceRef.current;
            const tableSource = source as TableChangeSource | undefined;

            const ignoreChange =
                !changes ||
                !hotInstance ||
                tableSource === TableChangeSource.LOAD_DATA ||
                tableSource === TableChangeSource.UPDATE_DATA ||
                tableSource === TableChangeSource.CELL_META_UPDATE_CELL_TYPE_EFFECT;

            if (ignoreChange) return;

            if (tableSource === TableChangeSource.UNDO || tableSource === TableChangeSource.REDO) {
                clearUndoRedoCellDataReplay(undoRedoRichCellDataState);
            }

            const sheet = hyperFormulaInstance.getSheetId(elementId);
            if (sheet === undefined) return;

            const data = cloneDeep(buildHotTableContentData(hotInstance));
            const visited = new WeakMap<object, boolean>();

            // These sources should NOT trigger the formula type re-inference for the current cell, but may for dependent cells
            const userTypeUpdateSources = [
                TableChangeSource.CELL_META_UPDATE_CELL_TYPE,
                TableChangeSource.CELL_META_UPDATE_CELL_TYPE_FORMAT,
            ];

            const applyCellChangesForFormulas = (
                _targetElementId: string,
                newChanges: CellDataChange[],
                cellAddress: SimpleCellAddress | SimpleCellRange,
                level = 0,
            ): void => {
                if (visited.get(cellAddress)) return;
                visited.set(cellAddress, true);

                const isDirectUpdateCellTypeChange =
                    userTypeUpdateSources.includes(tableSource as TableChangeSource) && level === 0;
                if (!isDirectUpdateCellTypeChange && isSimpleCellAddress(cellAddress)) {
                    const { row, col } = cellAddress;

                    const nextCellDataChanges: FormulaCellDataChanges = {};
                    const previousCellData = cloneDeep(data[row][col]);
                    const tempCellData = cloneDeep(previousCellData);

                    // Infer type
                    const currentType = tempCellData.type;
                    const inferredType = inferFormulaCellType(cellAddress, hotInstance);
                    if (inferredType && inferredType.name !== currentType?.name) {
                        nextCellDataChanges.type = inferredType;
                        tempCellData.type = inferredType;
                    }

                    // For formulas, save result value so that the cell result value can be indexed and searched
                    if (hyperFormulaInstance.getCellType(cellAddress) === CellType.FORMULA) {
                        const hotComputedCellValue = hyperFormulaInstance.getCellValue(cellAddress) as Parameters<
                            typeof normaliseComputedCellValue
                        >[0];
                        const normalisedComputedCellValue = normaliseComputedCellValue(hotComputedCellValue, locale, {
                            hfType: hyperFormulaInstance.getCellType(cellAddress),
                            hfDetailedType: hyperFormulaInstance.getCellValueDetailedType(cellAddress),
                        }) as CellData['value'];

                        tempCellData.value = normalisedComputedCellValue;

                        const currentResultValue = tempCellData.resultValue;
                        const resultValue = convertCellDataToReduxFormat(tempCellData, locale).value;

                        if (resultValue !== undefined && resultValue !== currentResultValue) {
                            nextCellDataChanges.resultValue = resultValue;
                        }
                    }

                    // Apply changes if cellDataChanges is not empty
                    if (!isEmpty(nextCellDataChanges)) {
                        const newCellData = {
                            ...omit(previousCellData, ['resultValue']),
                            ...nextCellDataChanges,
                        };

                        newChanges.push([row, col, previousCellData, newCellData]);
                    }
                }

                const cellDependents = hyperFormulaInstance.getCellDependents(cellAddress);
                cellDependents.forEach((cellDependent) => {
                    applyCellChangesForFormulas(_targetElementId, newChanges, cellDependent, level + 1);
                });
            };

            // This is supposed to determine any dependant cell that's impacted by a type change in a referenced
            //  cell and update the type of the dependant (see https://github.com/Milanote/milanote/pull/14866)
            // NOTE: There appears to be a bug in this logic - see https://linear.app/milanote/issue/WEB-15825/dependent-cell-types-no-longer-update
            const cellDataChanges: CellDataChange[] = [];

            changes.forEach(([row, col]) => {
                applyCellChangesForFormulas(elementId, cellDataChanges, { sheet, row, col: Number(col) });
            });

            if (cellDataChanges.length === 0) return;

            applyExactHotTableCellDataChanges(
                hotInstance,
                cellDataChanges,
                TableChangeSource.CELL_META_UPDATE_CELL_TYPE_EFFECT,
            );
        },
        [elementId, hotTableInstanceRef, locale, undoRedoRichCellDataState],
    );

    /**
     * Reconciles HOT grid structure changes with the cell data snapshot that Redux persistence will later save.
     */
    const afterGridAltered = useRefCallback(
        (axis: TableAxis, operation: TableOperation, index: number, amount: number, _source?: string) => {
            const hotInstance = hotTableInstanceRef.current;

            // Need to recalculate the autofill handle position after grid altered
            // e.g. bottom right corner may have previously been selected but isn't anymore since rows have been added below
            repositionAutofillHandle(hotInstance, hotTableContainerRef);

            if (!hotInstance) return;

            const rawTableContent = convertHotInstanceToRaw(hotInstance, gridSize);

            let formattedData = rawTableContent.data;

            if (operation === TableOperation.INSERT) {
                formattedData = continueCellFormatting({
                    dataArray: rawTableContent.data,
                    colIndex: axis === TableAxis.COL ? index : 0,
                    nColsAdded: axis === TableAxis.COL ? amount : 0,
                    rowIndex: axis === TableAxis.ROW ? index : 0,
                    nRowsAdded: axis === TableAxis.ROW ? amount : 0,
                });

                const nRows = formattedData.length;
                const nCols = formattedData[0]?.length || 0;

                applyHotTableCellMetaInRange({
                    hotInstance,
                    cellRange: getCellRangeFromStartAndSize(
                        axis === TableAxis.COL
                            ? { start: { row: 0, col: index }, size: { row: nRows, col: amount } }
                            : { start: { row: index, col: 0 }, size: { row: amount, col: nCols } },
                    ),
                    getCellMeta: ({ row, col }) => ({
                        cellData: formattedData[row][col],
                    }),
                });
            }
        },
        [gridSize, hotTableContainerRef, hotTableInstanceRef],
    );

    /**
     * Seeds Hyperformula for newly inserted rows or columns before the shared grid-altered flow refreshes formatting
     * and persistence metadata.
     */
    const afterGridInsert = useRefCallback<UseTableCellEditingHandlersReturn['afterGridInsert']>(
        (axis) => (index, amount, source) => {
            const hotInstance = hotTableInstanceRef.current;
            if (!hotInstance) return;

            // Make sure all cells have a value in Hyperformula, otherwise we will get
            // an error when trying to perform operations like setIndexesSequence
            // once https://github.com/handsontable/handsontable/issues/10569 has been resolved,
            // check if we can remove this
            const sheetId = hyperFormulaInstance.getSheetId(elementId) as number;
            const nCols = hotInstance.countCols();
            const nRows = hotInstance.countRows();

            // hyperFormulaInstance.setCellContents clears its redo queue
            // When redoing a grid insertion, this would cause the HF redo queue to fall out of
            //  sync with the HOT redo queue & errors to be thrown while attempting a redo
            // By preventing any HF redo stack changes, the HOF redo queue will remain in-sync
            runWithoutChangingHyperFormulaUndoRedoStack(hotInstance, () => {
                // Set new cells to empty in Hyperformula
                if (axis === TableAxis.COL) {
                    const newContentSingleRow = new Array(amount).fill('');
                    const newContent = new Array(nRows).fill(newContentSingleRow);
                    const firstNewCellAddress = { sheet: sheetId, row: 0, col: index };
                    hyperFormulaInstance.setCellContents(firstNewCellAddress, newContent);
                } else if (axis === TableAxis.ROW) {
                    const newContentSingleRow = new Array(nCols).fill('');
                    const newContent = new Array(amount).fill(newContentSingleRow);
                    const firstNewCellAddress = { sheet: sheetId, row: index, col: 0 };
                    hyperFormulaInstance.setCellContents(firstNewCellAddress, newContent);
                }
            });

            afterGridAltered(axis, TableOperation.INSERT, index, amount, source);
        },
        [afterGridAltered, elementId, hotTableInstanceRef],
    );

    /**
     * Routes row and column removals through the shared grid-altered reconciliation path.
     */
    const afterGridRemove = useRefCallback<UseTableCellEditingHandlersReturn['afterGridRemove']>(
        (axis) => (index, amount, _physicalRowsOrColumns, source) => {
            afterGridAltered(axis, TableOperation.REMOVE, index, amount, source);
        },
        [afterGridAltered],
    );

    /**
     * Stores the pre-move snapshot before HOT and HyperFormula update any index mapper state.
     */
    const beforeGridMovement = useRefCallback<UseTableCellEditingHandlersReturn['beforeGridMovement']>(
        (_axis) => (_movedIndices, _finalIndex, _dropIndex, movePossible) => {
            const hotInstance = hotTableInstanceRef.current;
            if (!movePossible || !hotInstance) {
                pendingBeforeMoveRef.current = null;
                return;
            }

            pendingBeforeMoveRef.current = {
                snapshot: convertHotInstanceToRaw(hotInstance, gridSize),
                hyperFormulaStack: captureHyperFormulaUndoRedoStack(hotInstance),
            };
        },
        [gridSize, hotTableInstanceRef],
    );

    /**
     * In Handsontable, moving rows/columns is treated as a temporary visual change, and only persists in the
     * session. The ordering is saved in `hotTableInstanceRef.current.rowIndexMapper.indexesSequence`.
     *
     * Since the persisted snapshot still stores row/column order directly in the table data, Milanote rewrites
     * HOT's source data into the visible moved order and records a snapshot-based undo action for that move.
     */
    const afterGridMovement = useRefCallback<UseTableCellEditingHandlersReturn['afterGridMovement']>(
        (axis) => (movedIndices, finalIndex, _dropIndex, movePossible, orderChanged) => {
            const hotInstance = hotTableInstanceRef.current;
            const beforeSnapshot = pendingBeforeMoveRef.current?.snapshot;
            const beforeHyperFormulaUndoRedoStack = pendingBeforeMoveRef.current?.hyperFormulaStack;

            pendingBeforeMoveRef.current = null;

            if (!movePossible || !orderChanged || !hotInstance || !beforeSnapshot) return;

            reconcileMovedGridWithUndoRedo({
                hotInstance,
                beforeSnapshot,
                axis,
                movedIndices,
                finalIndex,
                gridSize,
                beforeHyperFormulaUndoRedoStack,
            });
        },
        [gridSize, hotTableInstanceRef],
    );

    /**
     * Intercepts cell mouse-down interactions so embedded controls, element selection, multi-select shortcuts, and
     * single-cell re-entry into edit mode all behave like the surrounding Milanote selection model expects.
     */
    const beforeOnCellMouseDown = useRefCallback<UseTableCellEditingHandlersReturn['beforeOnCellMouseDown']>(
        (event, coords) => {
            const hotEvent = event as HotMouseEvent;
            if (hotEvent.isImmediatePropagationEnabled === false) return;

            const hotInstance = hotTableInstanceRef.current;
            if (!hotInstance) return;

            const target = event.target as HTMLElement | null;

            // If user clicks on a checkbox in a cell, don't select the cell
            if (target?.parentElement?.classList.contains('checkbox-animated')) {
                hotEvent.isImmediatePropagationEnabled = false;
            }

            if (target?.parentElement?.parentElement?.classList.contains('DraftLinkAnchor')) {
                // If a link has been clicked, don't select the cell
                event.stopImmediatePropagation();
                event.preventDefault();
                return false;
            }

            if (!isSingleSelected) {
                // If the table element is not yet selected, select the table element instead of the cell
                // this would only happen when the cover is not present (e.g. when the table is in mobile view)
                const tableElement = getTableElementFromHotInstance(hotInstance);
                if (!tableElement) return;

                // Both events are necessary to trigger the selection of the table element
                // In ElementContainer mouseDown alone will never select the element as there is an early
                // return for document mode, and click will only run if there has been a mouseDown event
                tableElement.dispatchEvent(new MouseEvent('mousedown', event));
                tableElement.click();
                return;
            }

            const isRightMouseClick = event.button === 2;
            if (isRightMouseClick) return;

            const hotCurrentCellSelections = hotInstance.getSelected();

            // On Cmd+click, and no cells are selected, deselect element
            if (
                (hasCommandModifier(event) || isControlKeyCommand(event)) &&
                !hasSelections(hotCurrentCellSelections || [])
            ) {
                event.stopImmediatePropagation();
                event.preventDefault();

                dispatchDeselectElement(elementId);
                return;
            }

            // On clicking the selected cell one more time, immediately go to edit mode.
            const currentSelection = getFirstSelection(hotCurrentCellSelections);
            if (!currentSelection) return;

            const isSelectingCurrentlySelectedCell =
                !!hotCurrentCellSelections &&
                isSelectingSingleSelection(hotCurrentCellSelections) &&
                isSelectingSingleCell(currentSelection) &&
                coords.row === getStartRow(currentSelection) &&
                coords.col === getStartCol(currentSelection);

            if (
                !(hasCommandModifier(event) || isControlKeyCommand(event)) &&
                isSelectingCurrentlySelectedCell &&
                hotInstance.getActiveEditor()
            ) {
                event.stopImmediatePropagation();
                event.preventDefault();

                hotInstance.getActiveEditor()?.beginEditing();
            }
        },
        [dispatchDeselectElement, elementId, hotTableInstanceRef, isSingleSelected],
    );

    return {
        beforeAutofill,
        beforeCopy,
        beforeCut,
        beforePaste,
        beforeChange,
        beforeGridMovement,
        afterChange,
        afterGridInsert,
        afterGridRemove,
        afterGridMovement,
        beforeOnCellMouseDown,
    };
};

export default useTableCellEditingHandlers;
